// An apply a page sent, while the shell follows its record (ADR-0480 decisions
// 3a and 4a, #2064). The page hands the `202`'s id over (`handOff`) and keeps
// it; when the shell's read of the record says it ended, the page is given what
// the route used to answer — the record's `result` on `done` — and draws it as
// it always drew that answer. Any other ending is said here, in the record's
// own words.
//
// **It reads nothing itself.** The shell (`AppliesFollowed`) reads the record
// when the change stream says an apply moved, when the stream reconnects, and
// when the operator moves between pages or writes; this only watches what
// those reads left in `following.ts`. No timer and no interval (§29).
import { useState } from "react";
import { APPLY_ENDED, handOff, useFollowedApply, wordsOf, type FollowedApply } from "./following";

/** What a page holds for the apply it handed over: `take` hands an answer over
 * and says whether it did; `waiting` is the record while it runs; `ended` is a
 * record that ended other than `done`, or could no longer be read. On `done`,
 * `onDone` is given the record's `result` once. */
export function useAcceptedApply<T>(onDone: (result: T) => void) {
  const [id, setId] = useState<string>();
  const [ended, setEnded] = useState<FollowedApply>();
  const f = useFollowedApply(id);
  const state = f?.record?.state;
  // Adjusting state while rendering, React's form for "information from previous renders":
  // `id` is cleared in the same pass, so this runs once per apply.
  if (id && f && (f.gone || (state !== undefined && APPLY_ENDED.has(state)))) {
    setId(undefined);
    if (state === "done" && !f.gone) onDone(f.record!.result as T);
    else setEnded(f);
  }
  const take = (r: { ok: boolean; status: number; data?: unknown }, what: string): boolean => {
    const got = handOff(r, what);
    if (got) { setId(got); setEnded(undefined); }
    return !!got;
  };
  const waiting: FollowedApply | undefined = id ? (f ?? { id, what: "" }) : undefined;
  return { take, waiting, ended, clear: () => { setId(undefined); setEnded(undefined); } };
}

/** The page's line for an apply it handed over: running, or how it ended other
 * than `done`. Nothing for neither. */
// Restates ADR-0480 decisions 3a, 4a and 5a, and ADR-0536 decision 1a — the answer is Zenith
// accepting the apply; what the controller did is the record's, read when the server says it moved;
// a record still open is never shown as applied, and one whose process is gone ends as stopped,
// said here in the record's own words (wordsOf), with nothing resumed.
export function AcceptedApplyNotice({ waiting, ended }: { waiting?: FollowedApply; ended?: FollowedApply }) {
  if (waiting) {
    return (
      <p className="notice" role="status" data-apply-waiting>
        Accepted. Zenith is sending it now; what the controller answers shows here once the server says the apply has
        ended, and under Applies at the top of every page.
      </p>
    );
  }
  if (!ended) return null;
  const w = wordsOf(ended);
  return <p className="notice" role="status" data-apply-ended={ended.gone ? "gone" : ended.record?.state}>Apply: {w.word}.{w.detail ? ` ${w.detail}` : ""}</p>;
}
